// Package imports
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
  createContext,
  useRef
} from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { default as ReactPagination } from "react-js-pagination";
import classNames from "classnames";
import get from "lodash/get";
import has from "lodash/has";
import set from "lodash/set";
import unset from "lodash/unset";
import isEmpty from "lodash/isEmpty";
import moment from "moment";

// Local imports
import { parseIsoDate } from "../../utilities/dates";
import {
  getDistanceBetween,
  getPostcodeData
} from "../../utilities/geospatial";
import { setUrlParam } from "../../utilities/urls";
import useViewport from "../../utilities/useViewport";

// Set up context to pass `state` and `callback` (reducer dispatch) variables
// to all stateful compound components.
const ListingContext = createContext();

/**
 * Filters an array of item objects and returns a subset, per a given filter.
 * @param {array} items Array ob item objects to be filtered
 * @param {Object} filter Filter object
 * @returns {Array}
 */
const getFilteredItems = (items, filter) => {
  if (Object.keys(filter).length === 0) {
    return items;
  }

  const newFilteredItems = items.filter(item => {
    let matchedCategories = 0;

    Object.keys(filter).forEach(category => {
      let matches = false;

      if (category === "search") {
        filter.search.fields.forEach(field => {
          if (!has(item, field)) {
            return;
          }
          const fieldText = get(item, field, "").toUpperCase();
          const searchTerm = filter.search.value.toUpperCase();
          if (fieldText.indexOf(searchTerm) > -1) {
            matches = true;
          }
        });
      } else if (category === "date") {
        const startDate = parseIsoDate(filter.date.start);
        const endDate = parseIsoDate(filter.date.end);

        const itemDate = moment(
          `${item.publishDay} ${item.publishMonth} ${item.publishYear}`,
          "DD-MM-YYYY"
        );

        if (startDate && endDate) {
          if (itemDate >= startDate && itemDate <= endDate) {
            matches = true;
          }
        } else if (startDate) {
          if (itemDate >= startDate) {
            matches = true;
          }
        } else if (endDate) {
          if (itemDate <= endDate) {
            matches = true;
          }
        }
      } else if (category === "geolocation") {
        const { withinDistance, postcode } = filter[category];
        if (postcode.length < 4) {
          matches = true;
        } else {
          const {
            postcode: foundPostcode,
            latitude: lat1,
            longitude: lon1
          } = getPostcodeData(postcode);
          if (foundPostcode) {
            const { latitude: lat2, longitude: lon2 } = item;
            const itemDistance = getDistanceBetween(lat1, lon1, lat2, lon2);
            if (itemDistance <= withinDistance) {
              matches = true;
            }
          }
        }
      } else {
        filter[category].forEach(filteredTerm => {
          if (item[category] && item[category].value.includes(filteredTerm)) {
            matches = true;
          }
        });
      }

      if (matches) {
        matchedCategories += 1;
      }
    });

    /**
     * Only include an item in the `filteredItems` Array if the item
     * matched at least one term in *each* of the filtered categories.
     */
    if (matchedCategories === Object.keys(filter).length) {
      return true;
    }

    return false;
  });

  return newFilteredItems;
};

/**
 * Slices an array of item objects and returns a paginated subset, given a page
 * number and specifying the total number of items per page.
 * @param {Array} items Array of item objects to be paginated
 * @param {number} activePage The page to retrieve items for
 * @param {number} totalItemsPerPage The total number of items per page
 * @returns {Array}
 */
const getPaginatedItems = (items, activePage, totalItemsPerPage) => {
  const startIndex = totalItemsPerPage * (activePage - 1);
  const endIndex = startIndex + totalItemsPerPage;
  return items.slice(startIndex, endIndex);
};

/**
 * Scrapes the top-level properties of an array of item objects and returns a
 * unique, sorted list of terms, given a category name.
 * @param {string} category The name of the category
 * @param {Array} items Array of item objects to scrape terms for
 * @returns {Array}
 */
export const generateTermsList = (category, items) => {
  const termsList = [];
  items
    .filter(item => item[category])
    .forEach(item => {
      termsList.push(...item[category].value);
    });
  termsList.sort();
  return Array.from(new Set(termsList));
};

/**
 * Initialises state for the listing reducer.
 * @param {Object} param0 State object
 * @returns
 */
export const initState = ({
  filter = {},
  allItems = [],
  filtersCollapsed = true,
  activePage = 1,
  totalItemsPerPage = 12,
  filtersList = [],
  filtersExpanded = []
}) => {
  const url = new URL(window.location);

  if (url.searchParams.has("filter")) {
    filter = JSON.parse(url.searchParams.get("filter"));
  } else if (Object.keys(filter).length > 0 && !url.searchParams.has("page")) {
    // If filter was pre-defined by us, then replace the history instead of push.
    url.searchParams.set("filter", JSON.stringify(filter));
    window.history.replaceState(null, null, url);
  }

  const filteredItems = getFilteredItems(allItems, filter);

  return {
    filter,
    allItems,
    filteredItems,
    paginatedItems: getPaginatedItems(
      filteredItems,
      activePage,
      totalItemsPerPage
    ),
    activePage,
    filtersCollapsed,
    totalItemsPerPage,
    paginationHasBeenClicked: false,
    filtersList,
    filtersExpanded
  };
};

/**
 * Reducer that manages all complex state logic for the listing component.
 * @param {Object} state React state object
 * @param {Object} action Reducer action, passed by dispatch
 * @returns {Object}
 */
export const listingReducer = (state, action) => {
  switch (action.type) {
    case "update filter with new selections": {
      const newFilter = { ...state.filter };
      const { category, term } = action.payload;

      /**
       * Adds a new condition to the filter
       * @param {Array} objectPath Object path in Array format
       * @param {*} value Value to be stored
       */
      const addToFilter = (objectPath, value) => {
        set(newFilter, objectPath, value);
      };

      /**
       * Removes a condition from the filter. Will also recursively ascend the
       * object path and remove any found empty objects.
       * @param {Array} objectPath Object path in Array format
       */
      const removeFromFilter = objectPath => {
        unset(newFilter, objectPath);
        if (Array.isArray(objectPath)) {
          const reducePath = [...objectPath];
          reducePath.pop();
          let pathCount = reducePath.length;
          while (pathCount > 0) {
            if (isEmpty(get(newFilter, reducePath))) {
              unset(newFilter, reducePath);
            }
            reducePath.pop();
            pathCount = reducePath.length;
          }
        }
      };

      if (category === "search") {
        if (term.value) {
          addToFilter([category], term);
        } else {
          removeFromFilter([category]);
        }
      } else if (category === "date") {
        if (term.date) {
          addToFilter([category, term.type], term.date);
        } else {
          removeFromFilter([category, term.type]);
        }
      } else if (category === "geolocation") {
        if (term.postcode) {
          const { postcode, withinDistance } = term;
          addToFilter([category], { postcode, withinDistance });
        } else {
          removeFromFilter([category]);
        }
      } else {
        // else if CheckboxTerms...
        if (term.selected) {
          if (newFilter.hasOwnProperty(category)) {
            newFilter[category] = [...newFilter[category], term.value];
          } else {
            newFilter[category] = [term.value];
          }
        } else {
          if (newFilter[category]) {
            if (newFilter[category].length === 1) {
              delete newFilter[category];
            } else {
              newFilter[category] = newFilter[category].filter(
                currentTerm => currentTerm !== term.value
              );
            }
          }
        }
      }

      const filterParam = { ...newFilter };
      if (Object.keys(filterParam).includes("search")) {
        delete filterParam["search"];
      }
      setUrlParam(["filter", filterParam], ["page", 1]);

      const filteredItems = getFilteredItems(state.allItems, newFilter);

      return {
        ...state,
        filter: newFilter,
        filteredItems,
        paginatedItems: getPaginatedItems(
          filteredItems,
          1,
          state.totalItemsPerPage
        ),
        activePage: 1
      };
    }

    case "reset filter": {
      setUrlParam(["filter"], ["page", 1]);
      return {
        ...state,
        filter: new Object({}),
        filteredItems: state.allItems,
        activePage: 1,
        paginatedItems: getPaginatedItems(
          state.allItems,
          1,
          state.totalItemsPerPage
        )
      };
    }

    case "set active page": {
      const { pageNumber, scrollToTop, skipPushState } = action.payload;
      const navigatingForwards = skipPushState === false;
      const navigatingBackwards = skipPushState === true;

      if (scrollToTop) {
        setTimeout(() => {
          document
            .getElementById("main")
            .scrollIntoView({ behavior: "smooth" });
        }, 100);
      }

      const paginationHasBeenClicked =
        !state.paginationHasBeenClicked && pageNumber > 1
          ? true
          : state.paginationHasBeenClicked;

      const url = new URL(window.location);

      if (navigatingForwards) {
        if (pageNumber !== state.activePage) {
          setUrlParam(["page", pageNumber]);
        }
      }

      if (navigatingBackwards) {
        if (url.searchParams.has("filter")) {
          state.filter = JSON.parse(url.searchParams.get("filter"));
        } else {
          state.filter = new Object({});
        }
        state.filteredItems = getFilteredItems(state.allItems, state.filter);
      }

      return {
        ...state,
        paginationHasBeenClicked,
        activePage: pageNumber,
        paginatedItems: getPaginatedItems(
          state.filteredItems,
          pageNumber,
          state.totalItemsPerPage
        )
      };
    }

    // Toggle the visbility of all the filters on mobile
    case "toggle filter collapse": {
      return {
        ...state,
        filtersCollapsed: !state.filtersCollapsed
      };
    }

    // Toggle the visibility of a single filter
    case "toggle filter expand": {
      const filtersExpanded = [...state.filtersExpanded];
      const { category } = action.payload;

      if (!filtersExpanded.includes(category)) {
        filtersExpanded.push(category);
      } else {
        filtersExpanded.splice(filtersExpanded.indexOf(category), 1);
      }

      return {
        ...state,
        filtersExpanded
      };
    }

    // Add category to an array list keeping track of all available filters
    // This list is populated everytime each checkbox filter is loaded
    // This is mainly for the expand all functionality
    case "add category to filter list": {
      const filtersList = [...state.filtersList];
      const { category } = action.payload;

      if (!filtersList.includes(category)) {
        filtersList.push(category);
      }

      return {
        ...state,
        filtersList
      };
    }

    // Show all filters
    case "expand all filters": {
      const filtersExpanded = [...state.filtersList];

      return {
        ...state,
        filtersExpanded
      };
    }

    // Hides all filters
    case "collapse all filters": {
      return {
        ...state,
        filtersExpanded: []
      };
    }

    default:
      throw new Error();
  }
};

// =============================================================================
// STATEFUL COMPOUND COMPONENTS
// =============================================================================

const ListingFilter = ({ children, callback, state }) => {
  useEffect(() => {
    ["load"].forEach(event => {
      window.addEventListener(event, () => {
        const url = new URL(window.location);
        callback({
          type: "set active page",
          payload: {
            pageNumber: +(url.searchParams.get("page") ?? 1),
            skipPushState: false
          }
        });
      });
    });
    ["popstate"].forEach(event => {
      window.addEventListener(event, () => {
        const url = new URL(window.location);
        callback({
          type: "set active page",
          payload: {
            pageNumber: +(url.searchParams.get("page") ?? 1),
            skipPushState: true
          }
        });
      });
    });
  }, []);

  return (
    <ListingContext.Provider value={{ state, callback }}>
      <div className="listing-filter">{children}</div>
    </ListingContext.Provider>
  );
};

ListingFilter.FiltersCollapse = function FiltersCollapse({
  children,
  className = ""
}) {
  const { state, callback } = useContext(ListingContext);

  const filtersCount = Object.keys(state.filter).reduce(
    (acc, key) =>
      acc + (Array.isArray(state.filter[key]) ? state.filter[key].length : 1),

    0
  );

  const screenIsMobile = () => window.matchMedia("(max-width: 768px)").matches;
  const [useCollapse, setUseCollapse] = useState(screenIsMobile());
  const handleResize = () => {
    setUseCollapse(screenIsMobile());
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      {useCollapse && (
        <button
          className={`btn-collapse ${
            state.filtersCollapsed ? "mb-0" : "open mb-3"
          } ${className}`}
          type="button"
          onClick={() => {
            callback({ type: "toggle filter collapse" });
          }}
        >
          <div className="d-flex align-items-center justify-content-between w-full">
            Filters
            {filtersCount !== 0 && (
              <span className="strong text-secondary">
                ({filtersCount} applied)
              </span>
            )}
          </div>
        </button>
      )}
      {(!state.filtersCollapsed && useCollapse) || !useCollapse ? (
        <ListingFilter.ToggleFilters />
      ) : null}

      {(!state.filtersCollapsed && useCollapse) || !useCollapse
        ? children
        : null}
    </>
  );
};

ListingFilter.ToggleFilters = function TogggleFilters() {
  const { state, callback } = useContext(ListingContext);

  return (
    <div className="listing-filter__toggle-all">
      <h2 className="listing-filter__section-title m-0">Filters</h2>

      <div className="listing-filter__toggle-all-btns">
        {state.filtersList.length !== state.filtersExpanded.length && (
          <button
            className="listing-filter__toggle-all-btn listing-filter__toggle-all-btn--expand m-0"
            onClick={() => {
              return callback({ type: "expand all filters" });
            }}
            type="button"
          >
            Expand all
          </button>
        )}

        {state.filtersExpanded.length !== 0 && (
          <button
            className="listing-filter__toggle-all-btn listing-filter__toggle-all-btn--collapse m-0"
            onClick={() => {
              return callback({ type: "collapse all filters" });
            }}
            type="button"
          >
            Collapse all
          </button>
        )}
      </div>
    </div>
  );
};

ListingFilter.CheckboxTerms = function CheckboxTerms({
  title,
  terms,
  category,
  showAll,
  collapsable = true,
  capitalise = false
}) {
  const { state, callback } = useContext(ListingContext);

  // Make a copy of the terms array so we don't modify the original
  const currentTerms = [...terms];

  if (!showAll && currentTerms.indexOf("all") > -1) {
    currentTerms.splice(currentTerms.indexOf("all"), 1);
  }

  const selectedTermCount =
    category in state.filter ? state.filter[category].length : 0;

  const isExpanded = state.filtersExpanded.includes(category) ? true : false;

  const termIsChecked = term => {
    if (!has(state, `filter[${category}]`)) {
      return false;
    }
    return get(state, `filter[${category}]`).includes(term);
  };

  // On first render, add this category to list of filters
  // This list of filters array is used to allow the "expand all filters functionality"
  useEffect(() => {
    callback({
      type: "add category to filter list",
      payload: {
        category
      }
    });
  }, []);

  return (
    <div className="listing-filter__section">
      {title && collapsable && (
        <button
          className={`listing-filter__title collapsible ${
            isExpanded ? "expanded" : "collapsed"
          }`}
          type="button"
          onClick={() => {
            return callback({
              type: "toggle filter expand",
              payload: {
                category
              }
            });
          }}
          id={`topics-filters-container-btn--` + category}
          aria-controls={`topics-filters-container--` + category}
        >
          <span>{title}</span>{" "}
          {selectedTermCount > 0 && (
            <span className="listing-filter__counter">{selectedTermCount}</span>
          )}
        </button>
      )}

      {title && !collapsable && (
        <div className="listing-filter__title">
          <span>{title}</span>{" "}
          {selectedTermCount > 0 && (
            <span className="listing-filter__counter">{selectedTermCount}</span>
          )}
        </div>
      )}

      <div
        id={`topics-filters-container--` + category}
        className={`topics-filter-container`}
        aria-labelledby={`topics-filters-container-btn--` + category}
      >
        {currentTerms.map((term, index) => {
          // Check if the term is defined, in case a term has come through
          // as undefined via a .map() due to a broken/faulty data set.
          if (term) {
            const isChecked = termIsChecked(term);
            const newValue = !isChecked;
            // Set the initial button classes
            const buttonClasses = classNames(
              "btn-filter",
              { "text-capitalize": capitalise },
              { selected: isChecked }
            );
            return useMemo(() => {
              return (
                <button
                  key={index}
                  className={buttonClasses}
                  type="button"
                  onClick={() => {
                    return callback({
                      type: "update filter with new selections",
                      payload: {
                        category,
                        term: {
                          value: term,
                          selected: newValue
                        }
                      }
                    });
                  }}
                >
                  {term}
                </button>
              );
            }, [newValue]);
          }
        })}
      </div>
    </div>
  );
};

ListingFilter.SelectedCheckboxFilters = function SelectedCheckboxFilters({}) {
  const { state, callback } = useContext(ListingContext);

  const filterKeys = Object.keys(state.filter);

  // Remove non checkbox filter keys
  const checkboxFilters = filterKeys.filter(category => {
    if (
      category !== "date" &&
      category !== "search" &&
      category !== "geolocation"
    ) {
      return true;
    }
  });

  const checkBoxFilterIsSet = checkboxFilters.length > 0;

  const termIsChecked = (term, category) => {
    if (!has(state, `filter[${category}]`)) {
      return false;
    }
    return get(state, `filter[${category}]`).includes(term);
  };

  let selectedFilterButtons = [];

  // Check if we have checkbox filters set
  if (checkBoxFilterIsSet) {
    checkboxFilters.forEach(category => {
      const filterBtn = state.filter[category].map((term, index) => {
        const isChecked = termIsChecked(term, category);

        const newValue = !isChecked;

        // Set the initial button classes
        const buttonClasses = classNames(
          "clearButton",
          "white",
          "text-capitalize"
        );

        return (
          <button
            key={index}
            className={buttonClasses}
            type="button"
            onClick={() => {
              return callback({
                type: "update filter with new selections",
                payload: {
                  category,
                  term: {
                    value: term,
                    selected: newValue
                  }
                }
              });
            }}
          >
            {term}
          </button>
        );
      });

      selectedFilterButtons.push(filterBtn);
    });
  }

  // Output the filter cancel buttons
  return (
    <>
      {checkBoxFilterIsSet && (
        <>
          <h2 className="listing-filter__section-title m-0">
            Selected filters
          </h2>
          {selectedFilterButtons}
        </>
      )}
    </>
  );
};

ListingFilter.DateRange = function DateRange({
  title,
  startDate,
  endDate,
  startDateMin = null,
  startDateMax = null,
  endDateMin = null,
  endDateMax = null,
  collapsable = true
}) {
  const { state, callback } = useContext(ListingContext);

  startDate = parseIsoDate(startDate || state.filter.date?.start);
  endDate = parseIsoDate(endDate || state.filter.date?.end);
  startDateMin = parseIsoDate(startDateMin);
  startDateMax = parseIsoDate(endDate || startDateMax);
  endDateMin = parseIsoDate(startDate || endDateMin);
  endDateMax = parseIsoDate(endDateMax);

  const isExpanded = state.filtersExpanded.includes("date") ? true : false;

  // On first render, add this category to list of filters
  // This list of filters array is used to allow the "expand all filters functionality"
  useEffect(() => {
    callback({
      type: "add category to filter list",
      payload: {
        category: "date"
      }
    });
  }, []);

  const datepicker = useMemo(
    () => (
      <div className="datepickers-wrapper">
        <label id="start-date-label">Choose start date</label>
        <DatePicker
          autoComplete="off"
          selected={startDate}
          onChange={date => {
            callback({
              type: "update filter with new selections",
              payload: {
                category: "date",
                term: { type: "start", date: moment(date).toISOString() }
              }
            });
          }}
          startDate={startDate}
          dateFormat={"dd/MM/yyyy"}
          minDate={startDateMin}
          maxDate={startDateMax}
          placeholderText={"Start"}
          popperClassName={"start-datepicker"}
          id={"start-datepicker-input"}
          ariaLabelledBy={"start-date-label"}
          wrapperClassName={"start-datepicker-wrapper"}
          popperModifiers={{
            preventOverflow: {
              enabled: true
            }
          }}
        />
        <span className="date-chevron">to</span>
        <label id="end-date-label" tabIndex="0">
          Choose end date
        </label>
        <DatePicker
          autoComplete="off"
          selected={endDate}
          onChange={date => {
            callback({
              type: "update filter with new selections",
              payload: {
                category: "date",
                term: { type: "end", date: moment(date).toISOString() }
              }
            });
          }}
          endDate={startDate}
          dateFormat={"dd/MM/yyyy"}
          placeholderText={"End"}
          id={"end-datepicker-input"}
          minDate={endDateMin}
          maxDate={endDateMax}
          popperClassName={"end-datepicker-popper"}
          popperModifiers={{
            preventOverflow: {
              enabled: true
            }
          }}
          ariaLabelledBy={"end-date-label"}
        />
      </div>
    ),
    [startDate, endDate]
  );

  return (
    <div className="listing-filter__section">
      {title && collapsable && (
        <button
          className={`listing-filter__title collapsible ${
            isExpanded ? "expanded" : "collapsed"
          }`}
          type="button"
          onClick={() => {
            return callback({
              type: "toggle filter expand",
              payload: {
                category: "date"
              }
            });
          }}
        >
          <span>{title}</span>
        </button>
      )}

      {title && !collapsable && (
        <div className="listing-filter__title">{title}</div>
      )}

      {datepicker}
    </div>
  );
};

ListingFilter.Geolocation = function Geolocation({ title, withinDistance }) {
  const { state, callback } = useContext(ListingContext);
  const value = get(state, `filter[geolocation][postcode]`, "");
  return useMemo(
    () => (
      <div className="listing-filter__section">
        {title && <div className="listing-filter__title">{title}</div>}
        <label htmlFor="postcode-search" id="postcode-search-label">
          Postcode
        </label>
        <input
          className="listing-filter__geolocation"
          type="number"
          value={value}
          onChange={event => {
            callback({
              type: "update filter with new selections",
              payload: {
                category: "geolocation",
                term: {
                  postcode: event.target.value.slice(0, 4),
                  withinDistance
                }
              }
            });
          }}
          placeholder="Postcode"
          id="postcode-search"
        />
      </div>
    ),
    [value]
  );
};

ListingFilter.ClearButton = function ClearButton() {
  const { state, callback } = useContext(ListingContext);

  const filterIsSet = Object.keys(state.filter).length > 0;

  return useMemo(
    () => (
      <>
        {filterIsSet && (
          <div className="listing-filter__selected">
            <ListingFilter.SelectedCheckboxFilters />
            <button
              className={`primary blue clearButton`}
              type="button"
              onClick={event => {
                callback({ type: "reset filter" });
              }}
            >
              Clear all filters
            </button>
          </div>
        )}
      </>
    ),
    [filterIsSet]
  );
};

ListingFilter.Pagination = function Pagination({
  linkClass = "default",
  linkClassFirst = "first",
  linkClassLast = "last",
  itemClassPrev = "prev-container",
  linkClassPrev = "previous",
  itemClassNext = "next-container",
  linkClassNext = "next",
  disabledClass = "no-more-items",
  activeClass = "active",
  firstPageText = "First",
  lastPageText = "Last",
  nextPageText = "Next",
  prevPageText = "Prev",
  hideDisabled = true
}) {
  const { state, callback } = useContext(ListingContext);
  const { activePage, totalItemsPerPage } = state;
  const totalItemsCount = state.filteredItems.length;
  const totalPages = Math.ceil(totalItemsCount / totalItemsPerPage);
  const { name: viewportName } = useViewport();
  const pageRangeDisplayed = viewportName === "xs" ? 3 : 5;

  if (totalPages === 1) {
    return null;
  }

  return (
    <>
      <ReactPagination
        activePage={activePage}
        itemsCountPerPage={totalItemsPerPage}
        totalItemsCount={totalItemsCount}
        pageRangeDisplayed={pageRangeDisplayed}
        onChange={pageNumber => {
          callback({
            type: "set active page",
            payload: { pageNumber, scrollToTop: true, skipPushState: false }
          });
        }}
        linkClass={linkClass}
        linkClassFirst={linkClassFirst}
        linkClassLast={linkClassLast}
        itemClassPrev={itemClassPrev}
        linkClassPrev={linkClassPrev}
        itemClassNext={itemClassNext}
        linkClassNext={linkClassNext}
        disabledClass={disabledClass}
        activeClass={activeClass}
        firstPageText={firstPageText}
        lastPageText={lastPageText}
        nextPageText={nextPageText}
        prevPageText={prevPageText}
        hideDisabled={hideDisabled}
      />
      {totalPages > 0 && (
        <span className="pagination-total-results">
          {`Page ${activePage} of ${totalPages}`}
        </span>
      )}
    </>
  );
};

ListingFilter.SearchFilter = function SearchFilter({
  title,
  placeholder,
  searchFields
}) {
  const { state, callback } = useContext(ListingContext);
  const [searchTerm, setSearchTerm] = useState("");

  // This ref starts with true until component finishes rendering.
  // This is because we don't want exectSearch to run at first render
  const isFirstLoad = useRef(true);

  const execSearch = useCallback(() => {
    callback({
      type: "update filter with new selections",
      payload: {
        category: "search",
        term: { fields: searchFields, value: searchTerm }
      }
    });
  }, [searchTerm]);

  // Perform exectSearch everytime input changes
  // This effect should not run at first render, only during subsequent renders
  useEffect(() => {
    if (!isFirstLoad.current) {
      execSearch();
    }
  }, [searchTerm]);

  // At first render, update isFirstLoad state to false
  useEffect(() => {
    isFirstLoad.current = false;
  }, []);

  return useMemo(
    () => (
      <div className="listing-filter__section listing-filter__search p-3 rounded-xl">
        <h2 className="listing-filter__title mt-0 text-white">{title}</h2>
        <input
          type="search"
          placeholder={placeholder}
          onChange={event => {
            setSearchTerm(event.target.value);
          }}
          value={searchTerm}
        />
      </div>
    ),
    [searchTerm]
  );
};

ListingFilter.NoResults = function NoResults({ children }) {
  const { state, callback } = useContext(ListingContext);
  if (state.paginatedItems && state.paginatedItems.length > 0) {
    return null;
  }
  return <>{children}</>;
};

// =============================================================================
// STATELESS COMPOUND COMPONENTS
// =============================================================================

ListingFilter.Card = function Card({
  colour,
  link,
  superHTML,
  titleText,
  contentHTML,
  image,
  featured
}) {
  let cardClasses = `listing-card emb-left-${colour} hoverable-${colour}`;
  let contentClasses = `position-static order-1 order-md-2 col-12`;

  if (image) {
    if (featured) {
      contentClasses = `${contentClasses} col-sm-6 col-md-8 col-lg-6`;
    } else {
      contentClasses = `${contentClasses} col-sm-9 col-md-8 col-lg-8`;
    }
  }

  const imageClasses = featured
    ? `col-sm-6 col-md-4 col-lg-6 order-sm-2 image-section`
    : `col-sm-3 col-md-4 col-lg-4 order-sm-2 image-section`;

  if (featured) {
    cardClasses = `${cardClasses} listing-card-featured`;
  }

  return (
    <div className={cardClasses}>
      <div className="row no-gutters">
        <div className={contentClasses}>
          <div className="listing-card-body">
            <div
              className="listing-card-super"
              dangerouslySetInnerHTML={{ __html: superHTML }}
            ></div>
            <h2 className="listing-card-title">
              <a className="stretched-link link-unstyled" href={link}>
                {titleText}
              </a>
            </h2>
            <div
              className="listing-card-content"
              dangerouslySetInnerHTML={{ __html: contentHTML }}
            />
          </div>
        </div>
        {image && (
          <div
            className={imageClasses}
            aria-label={titleText}
            style={{ backgroundImage: `url(${image})` }}
          ></div>
        )}
      </div>
    </div>
  );
};

ListingFilter.CardGroup = function CardGroup({
  children,
  contentHTML,
  showGroupHTML
}) {
  return (
    <div className="row no-gutters">
      {typeof contentHTML === "object" && (
        <div className="col-sm-12 col-md-2">{contentHTML}</div>
      )}
      {typeof contentHTML !== "object" && (
        <div
          className="col-sm-12 col-md-2"
          dangerouslySetInnerHTML={{ __html: contentHTML }}
        />
      )}
      <div className="col-sm-12 col-md-10 card-container">
        <div className="row">
          <div className="col-12">{children}</div>
        </div>
      </div>
    </div>
  );
};

ListingFilter.DateGroup = function DateGroup({ date }) {
  const theDate = moment(date, "DD/MM/YYYY");
  return (
    <div className="d-flex flex-column mb-2 pr-md-3 text-md-right">
      <span className="text-super">{theDate.format("YYYY")}</span>
      <span className="text-super">{theDate.format("MMMM")}</span>
      <span className="text-size-xl">{theDate.format("D")}</span>
    </div>
  );
};

export default ListingFilter;
